import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Music, Play, Square } from "lucide-react";
import { cn } from "../../lib/utils";

export interface AudioOption {
  id: string;
  name: string;
  preview_url?: string;
  [key: string]: any; // Allow other properties to pass through
}

export interface SelectedAudio extends AudioOption {
  volume: number;
}

interface MultiSelectAudioDropdownProps {
  label: string;
  options: AudioOption[];
  value: SelectedAudio[];
  onChange: (value: SelectedAudio[]) => void;
  className?: string;
  placeholder?: string;
}

export function MultiSelectAudioDropdown({
  label,
  options,
  value,
  onChange,
  className,
  placeholder = "Select sounds...",
}: MultiSelectAudioDropdownProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});
  
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Position logic (similar to standard Dropdown portal)
  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAX_H = 400;
    const below = window.innerHeight - r.bottom;
    const up = below < MAX_H + 12 && r.top > below;
    setStyle({
      position: "fixed",
      left: r.left,
      width: Math.max(r.width, 360), // Wider for audio controls
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      maxHeight: Math.min(MAX_H, (up ? r.top : below) - 12),
      zIndex: 70,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingId(null);
      }
      return;
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, []);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
    }
  };

  const playPreview = (opt: AudioOption, currentVolume: number) => {
    if (!opt.preview_url && !opt.background_url) return;
    const url = opt.preview_url || opt.background_url;

    if (playingId === opt.id) {
      stopPreview();
      return;
    }

    stopPreview();
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setPlayingId(null);
      audioRef.current.onerror = () => setPlayingId(null);
    }
    
    audioRef.current.src = url;
    audioRef.current.volume = currentVolume;
    audioRef.current.play().catch(() => setPlayingId(null));
    setPlayingId(opt.id);
  };

  const toggleSelection = (opt: AudioOption) => {
    const isSelected = value.some((v) => v.id === opt.id);
    if (isSelected) {
      onChange(value.filter((v) => v.id !== opt.id));
    } else {
      onChange([...value, { ...opt, volume: 0.3 }]);
    }
  };

  const updateVolume = (opt: AudioOption, volume: number) => {
    // Volume only applies to a track that's actually toggled on -- the slider
    // is disabled while it's off (see the <input type="range"> below), so this
    // should be unreachable, but guard it anyway rather than silently
    // re-enabling the track as a side effect of a volume change.
    const isSelected = value.some((v) => v.id === opt.id);
    if (!isSelected) return;
    onChange(value.map((v) => (v.id === opt.id ? { ...v, volume } : v)));

    if (playingId === opt.id && audioRef.current) {
      audioRef.current.volume = volume;
    }
  };

  const selectedSummary = value.length === 0
    ? placeholder
    : value.length === 1
      ? value[0].name
      : `${value.length} Sounds Active`;

  return (
    <div className={cn("relative", className)}>
      <button
        ref={btnRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border bg-elevated px-3 py-1.5 text-left text-xs transition-colors focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
          open ? "border-brand" : "border-line",
        )}
      >
        <span className={cn("block truncate", value.length === 0 && "text-ink-3")}>
          {selectedSummary}
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={style}
            role="listbox"
            aria-multiselectable="true"
            className="flex flex-col overflow-hidden rounded-xl border border-line bg-elevated shadow-xl animate-in fade-in slide-in-from-top-2"
          >
            <div className="flex flex-col overflow-y-auto overscroll-contain p-1">
              {options.length === 0 ? (
                <div className="p-3 text-center text-xs text-ink-3">No sounds available</div>
              ) : (
                options.map((opt, i) => {
                  const selectedObj = value.find((v) => v.id === opt.id);
                  const isSelected = !!selectedObj;
                  const volume = selectedObj ? selectedObj.volume : 0.3;
                  const isPlaying = playingId === opt.id;
                  const canPlay = !!(opt.preview_url || opt.background_url);

                  return (
                    <div
                      key={opt.id}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "group relative flex items-center justify-between gap-3 rounded-lg p-2 hover:bg-soft transition-colors",
                        isSelected && "bg-soft/50"
                      )}
                    >
                      {/* Metadata */}
                      <div className="flex items-center gap-2 flex-1 min-w-0 pl-1">
                        <Music className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                        <span className={cn("truncate text-xs font-medium", isSelected ? "text-ink" : "text-ink-2")}>
                          {opt.name}
                        </span>
                      </div>

                      {/* Preview & Volume Controls */}
                      <div className="flex items-center gap-3 shrink-0">
                        <button
                          type="button"
                          disabled={!canPlay}
                          onClick={(e) => {
                            e.stopPropagation();
                            playPreview(opt, volume);
                          }}
                          className={cn(
                            "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                            !canPlay && "opacity-50 cursor-not-allowed",
                            isPlaying
                              ? "bg-brand text-white hover:bg-brand-dark"
                              : "bg-soft text-ink hover:bg-line"
                          )}
                          aria-label={isPlaying ? `Pause ${opt.name}` : `Play ${opt.name}`}
                        >
                          {isPlaying ? (
                            <Square className="h-3 w-3 fill-current" />
                          ) : (
                            <Play className="h-3 w-3 ml-0.5 fill-current" />
                          )}
                        </button>

                        <div className="flex flex-col gap-1 w-20">
                          <label className="sr-only">Volume for {opt.name}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={volume}
                            disabled={!isSelected}
                            onChange={(e) => updateVolume(opt, parseFloat(e.target.value))}
                            className={cn("w-full accent-brand", !isSelected && "opacity-40 cursor-not-allowed")}
                            aria-label={`Volume, ${Math.round(volume * 10)} out of 10`}
                          />
                        </div>

                        {/* Toggle Switch */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isSelected}
                          onClick={() => toggleSelection(opt)}
                          className={cn(
                            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
                            isSelected ? "bg-brand" : "bg-line"
                          )}
                        >
                          <span className="sr-only">Toggle {opt.name}</span>
                          <span
                            className={cn(
                              "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                              isSelected ? "translate-x-4" : "translate-x-0"
                            )}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
